/*
Extensions
*/
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Link from "@tiptap/extension-link";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useState } from "react";
import { cn } from "~/lib/utils";

import { FontSizeExtension } from "./extensions/font-size";
import { SuggestionProps } from "./suggestion";
import { Hashtag } from "./suggestion/hashtag";
import Toolbar from "./toolbar";
import ToolbarButton from "./toolbar/btn";
import { useHashTagSuggestions } from "./useHashTagSuggestions";
import { useMentionSuggestions } from "./useMentionSuggestions";

interface AduseiEditorProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: SuggestionProps[];
  onExpand?: () => void;
  isExpanded?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
}

const AduseiEditor = ({
  value,
  onChange,
  suggestions,
  onExpand,
  isExpanded,
  autoFocus,
  className,
  placeholder,
  readOnly,
}: AduseiEditorProps) => {
  const [isFocused, setIsFocused] = useState(false); // Track focus state
  const mentionSuggestion = useMentionSuggestions("@", suggestions);
  const hashtagSuggestion = useHashTagSuggestions(suggestions);
  // useEffect(() => {
  //   if (editorState && !value) {
  //     editorState.commands.clearContent(true);
  //   }
  // }, [editorState, value]);
  // Updated useEffect to handle both clearing and setting content
  const editor = useEditor({
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: cn(
          "focus:outline-none bg-white flex flex-col w-full min-h-60 cursor-text",
          "p-3 whitespace-pre-wrap break-words",
          { "min-h-96": isExpanded },
        ),
      },
    },
    extensions: [
      StarterKit,
      Placeholder.configure({
        // Use a placeholder:
        placeholder,
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
      }),
      TextStyle,
      Color,
      Highlight.configure({
        multicolor: true,
      }),
      Underline,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      FontSizeExtension,
      Hashtag.configure({
        HTMLAttributes: {
          class: "hashtag highlight-hashtag",
        },
        suggestion: hashtagSuggestion,
        renderLabel({ node }) {
          return `#${node.attrs.label}`;
        },
      }),

      Mention.configure({
        HTMLAttributes: {
          class: "mention",
        },
        suggestion: mentionSuggestion,
        renderHTML({ node }) {
          return [
            "a",
            {
              class:
                "font-normal hover:underline hover:underline-offset-2 text-blue-500",
              userkey: node.attrs.id,
              "data-username": node.attrs.label,
              "data-linked-resource-type": "userinfo",
              href: node.attrs.id,
            },

            `${node.attrs.label}`,
          ];
        },
      }),
    ],
    onUpdate({ editor }) {
      // Only call onChange if the update was triggered by the user (focused)
      // to avoid feedback loops from programmatic setContent calls.
      if (editor.isFocused) {
        onChange(editor.getHTML());
      }
    },
    content: value,
    autofocus: autoFocus ? true : false,

    // Existing editor configuration
    onFocus() {
      setIsFocused(true); // Set focus state
    },
    onBlur() {
      setIsFocused(false); // Reset focus state
    },
  });

  useEffect(() => {
    if (editor && readOnly !== undefined) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  useEffect(() => {
    if (editor && value !== undefined && value !== null) {
      const currentContent = editor.getHTML();

      // Only update if the content is actually different and the value is not empty when we already have content
      // or if we need to explicitly clear the content
      if (currentContent !== value) {
        if (value === "" || value === "<p></p>") {
          editor.commands.clearContent(true);
        } else {
          editor.commands.setContent(value, false);
        }
      }
    }
  }, [editor, value]);
  const handleFocus = () => {
    if (editor) {
      const endPosition = editor.state.doc.content.size; // Get the end position of the content
      editor.chain().focus().setTextSelection(endPosition).run(); // Focus and move cursor
    }
  };

  return (
    <div
      className={cn(
        "w-full", // Always full width
        // Constrain only on small screens
        "overflow-x-auto rounded-2xl border border-neutral-200 bg-white print:overflow-visible",
        "focus-within:border-secondary-500 focus-within:ring-1 focus-within:ring-secondary-500",
        "p-2 sm:p-3", // Smaller padding on mobile
        className,
      )}
    >
      <div className="relative flex items-center justify-between px-2 py-1">
        {isFocused && !isExpanded && !readOnly && (
          <div
            className="pointer-events-auto absolute right-0 top-0 z-10"
            style={{
              pointerEvents: "all",
            }}
          >
            <ToolbarButton
              icon="Maximize"
              onClick={() => {
                if (onExpand) {
                  onExpand();
                }
                handleFocus();
              }}
              isActive={false}
              onMouseDown={(e) => e.preventDefault()} // Prevent focus loss
            />
          </div>
        )}
      </div>
      <div className="mx-auto flex justify-center">
        <EditorContent editor={editor} className="w-full" />
      </div>
      {!readOnly && <Toolbar editor={editor} />}
    </div>
  );
};

export default AduseiEditor;
